import { useCallback, useEffect, useRef, useState } from "react";

import {
  supabaseAuthenticatedApi,
  workspaceBackendUrl,
} from "../components/SupabaseClient";

export const useResourceAggregate = ({
  enabled = true,
  pollIntervalMs = null,
  refreshOnVisible = false,
} = {}) => {
  const [aggregate, setAggregate] = useState(null);
  const [loading, setLoading] = useState(Boolean(enabled));
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const controllerRef = useRef(null);
  const activeRef = useRef(true);

  useEffect(() => {
    activeRef.current = true;
    return () => {
      activeRef.current = false;
      controllerRef.current?.abort();
    };
  }, []);

  const refetch = useCallback(async () => {
    if (!enabled) {
      return null;
    }

    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoading(true);

    try {
      const response = await supabaseAuthenticatedApi.get(
        "/cluster/resources/aggregate",
        workspaceBackendUrl,
        10000,
        { signal: controller.signal },
      );
      const data = await response.json();

      if (!activeRef.current || controllerRef.current !== controller) {
        return data;
      }

      setAggregate(data);
      setError(null);
      setLastUpdated(Date.now());
      return data;
    } catch (fetchError) {
      if (
        activeRef.current &&
        controllerRef.current === controller &&
        fetchError.name !== "AbortError"
      ) {
        setError(fetchError.message || "Failed to load cluster resources.");
      }
      return null;
    } finally {
      if (activeRef.current && controllerRef.current === controller) {
        controllerRef.current = null;
        setLoading(false);
      }
    }
  }, [enabled]);

  useEffect(() => {
    if (!enabled) {
      controllerRef.current?.abort();
      setLoading(false);
      return;
    }

    refetch();
  }, [enabled, refetch]);

  useEffect(() => {
    if (!enabled || !pollIntervalMs) {
      return;
    }

    const id = setInterval(() => {
      if (typeof document === "undefined" || !document.hidden) {
        refetch();
      }
    }, pollIntervalMs);

    return () => clearInterval(id);
  }, [enabled, pollIntervalMs, refetch]);

  useEffect(() => {
    if (!enabled || !refreshOnVisible || typeof document === "undefined") {
      return;
    }

    const handleVisibilityChange = () => {
      if (!document.hidden) {
        refetch();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, refreshOnVisible, refetch]);

  return {
    aggregate,
    loading,
    error,
    lastUpdated,
    refetch,
  };
};
